import React, {useEffect, useState } from "react";

import GraphDataModel from "./GraphDataModel";
import { doGetRequest } from "../api/ApiUtils";
import { titleCase } from "../utils/FormatString";

import { 
    Container,
    Header,
    Form,
    SpaceBetween,
    FormField,
    Select,
    Button,
    Box,
    Link,
    Modal,
    Tabs,
    TextFilter,
    ColumnLayout,
    Popover,
    StatusIndicator,
    Hotspot,
    AnnotationContext,
  } from "@cloudscape-design/components"
import GraphViewer from "./GraphViewer";
import JournalFilterResultsViewer from "./JournalFilterResultsViewer";

export default () => {
  
    const [graphData, setGraphData] = useState([])
    const [selectedOption, setSelectedOption] = React.useState({label: "Select a disease ...", value: ""});
    const [diseaseList, setDiseaseList] = React.useState([]);
    const [visible, setVisible] = React.useState(false);
    const [journalFilter, setJournalFilter] = React.useState("");
    const [selectedJournalFilterType, setSelectedJournalFilterType] = React.useState({ label: "Property search (Journal title and abstract)", value: "basic" });
    const [isDiseaseHierarchyLoading, setIsDiseaseHierarchyLoading] = React.useState(false);
    const [selectedJournalDiseaseFilterType, setSelectedJournalDiseaseFilterType] = React.useState({ label: "No - just search using the search method", value: ""});
    const [journalFilterErrorText, setJournalFilterErrorText] = React.useState("");
    const [journalTableResults, setJournalTableResults] = React.useState([])
    const [journalTableResultsIsLoading, setJournalTableResultsIsLoading] =  React.useState(false);
    const [journalPageIndex, setJournalPageIndex] = React.useState(0);
    const [includeDiseaseOntologyDisabled, setIncludeDiseaseOntologyDisabled] = React.useState(false);
    const [constraintText, setConstraintText] = React.useState("Examples: 'vascular disease', 'infectious agent'")
    const [selectedGraphViewStyle, setSelectedGraphViewStyle] = useState({label: "Concentric", value: "concentric"})
    const [graphViewStyle, setGraphViewStyle] = useState("concentric")


    useEffect(() => {
        getDiseaseList();
    }, []);

    
    const showDiseaseHierarchy = (option) => {
      setSelectedOption(option);
      setIsDiseaseHierarchyLoading(true);
      doGetRequest('get-disease-hierarchy', `diseaseId=${option.value}&limit=50`, false, (data) => {
        setGraphData(data.elements);

        setIsDiseaseHierarchyLoading(false);
      });
    }

    const getDiseaseList = () => {
        doGetRequest('get-diseases', null, false, (data) => {
            const d = JSON.parse(data);

            const list = d.results.map(element => {
              return ({label: titleCase(element.diseaseName), value: element.diseaseId})
            });
            
            setDiseaseList([{label: "Select a disease ...", value: ""}, ...list]);
        });
    }
    
    const doJournalFilter = () => {
      // this will run when they submit the form
      if (journalFilter.trim() !== "" && selectedJournalFilterType !== "") {
        setJournalTableResultsIsLoading(true);
        setJournalFilterErrorText("");

        // we need to return the results as a table
        doGetRequest('filter-journals',`q=${journalFilter}&t=${selectedJournalFilterType.value}&i=${selectedJournalDiseaseFilterType.value}&o=tabular&l=20`, false, (data) => {
          //console.log(data);

          setJournalPageIndex(1);
          setJournalTableResults(data);
          setJournalTableResultsIsLoading(false);
        });


        // next we want to return the results as a graph - only if we're using vectors as a search type
        if (selectedJournalFilterType.value === 'vector') {
          doGetRequest('filter-journals',`q=${journalFilter}&t=${selectedJournalFilterType.value}&i=${selectedJournalDiseaseFilterType.value}&o=graph&l=20`, false, (data) => {

          });
        }
        
      }
      else {
        if (journalFilter.trim() === "") {
          setJournalFilterErrorText("Please provide a search term.");
        }
      }
    }   

    return (
      <div>
        <Modal
          onDismiss={() => setVisible(false)}
          visible={visible}
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" onClick={() => setVisible(false)}>Close</Button>
              </SpaceBetween>
            </Box>
          }
          header="Disease Ontology Graph Model">
            <GraphDataModel></GraphDataModel>
        </Modal>
        <Container className="body-container">
          {/* <br></br> */}
          <Tabs
            tabs={[
            {
              label: "Journal Search",
              id: "search",
              content: 
                <Container
                header={
                  <Header
                    variant="h2"
                    description="Use the options below to search for medical publication journals in our graph data. 
                    Notice that, by choosing to apply the search filter against the disease ontology in the graph data, the results become more relevant."
                  >
                    Search the graph for Journals

                  </Header>}
                >
                  <form onSubmit={e => {e.preventDefault();}}>
                  <Form
                    actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="primary" onClick={(e) => {doJournalFilter()}}>Submit</Button>
                      </SpaceBetween>
                    }>
                      <FormField label="Journal search:"
                        constraintText={constraintText}
                        variant="embedded" errorText={journalFilterErrorText}>
                        <TextFilter
                          filteringText={journalFilter}
                          filteringPlaceholder="Search for a journal ..."
                          onChange={( {detail}) => { setJournalFilter(detail.filteringText)}}
                        ></TextFilter>
                      </FormField>
                      <ColumnLayout columns={4}>
                        <SpaceBetween direction="vertical" size="l">
                          <FormField label="Search method:">
                            <Select
                              selectedOption={selectedJournalFilterType}
                              onChange={({detail}) => { 
                                  setSelectedJournalFilterType(detail.selectedOption); 
                                  if (detail.selectedOption.value === 'basic') {
                                    setIncludeDiseaseOntologyDisabled(false)
                                    setConstraintText("Examples: 'vascular disease', 'infectious agent'")
                                  }
                                  else {
                                    setConstraintText("Examples: 'contagious diseases in 2020', 'treatments for rare conditions'")
                                    setIncludeDiseaseOntologyDisabled(true)
                                    setSelectedJournalDiseaseFilterType({ label: "No - just search using the search method", value: ""});
                                  }
                               }}
                              options={[
                                { label: "Property search (Journal title and abstract)", value: "basic" },
                                { label: "TopK search (Vector similarity)", value: "vector"}
                              ]}
                            ></Select>
                          </FormField>
                        </SpaceBetween>
                        <SpaceBetween direction="vertical" size="l">
                          <FormField label={
                              <>
                                <span>Include disease ontology:&nbsp;</span>
                                <Popover
                                  dismissButton={true}
                                  position="top"
                                  size="medium"
                                  triggerType="custom"
                                  content={
                                    <StatusIndicator type="info">
                                      Choosing 'Yes' will apply the search term against disease vertices in the<br/>graph model.
                                    </StatusIndicator>
                                  }
                                >
                                  <Button iconName="status-info" id="inline-popover-button"></Button>    
                                </Popover>
                                
                              </>}>
                            <Select selectedOption={selectedJournalDiseaseFilterType}
                                    onChange={({ detail }) => { setSelectedJournalDiseaseFilterType(detail.selectedOption) }}
                                    options={[
                                      { label: "Yes - include an ontology search", value: "y"},
                                      { label: "No - just search using the search method", value: ""}
                                    ]}
                                    disabled={includeDiseaseOntologyDisabled}
                                    >
                            </Select>
                          </FormField>
                        </SpaceBetween>
                      </ColumnLayout>
                  </Form>
                </form>
                <br/>
                <JournalFilterResultsViewer filter={journalFilter} pageIndex={journalPageIndex} items={journalTableResults} isLoading={journalTableResultsIsLoading}></JournalFilterResultsViewer>
              </Container>
            },
            {
              label: "Exploring the Disease Ontology",
              id: "explore",
              content: 
              <Container
              header={
                <Header
                  variant="h2"
                  description="The Disease Ontology provides a cross mapping between diseases and medical vocabulary, defining how each disease relates to (i.e. is a subClassOf) another disease. Run the following query to find diseases that are one level below the top-level disease. These diseases represent high-level classes of diseases."
                  info={<Link variant="info" onFollow={() => setVisible(true)}>View the graph model</Link>}
                >
                  Exploring the Disease Ontology
                </Header>
              }
          >
            <div className="graph-container" >
              <form onSubmit={e => e.preventDefault()}>
                <Form>
                  <SpaceBetween direction="horizontal" size="xs">
                    <FormField label="Disease selection:">
                      <div style={{width: '300px'}}>
                        <Select selectedOption={selectedOption}
                                autosize={true}
                                onChange={({ detail }) => { showDiseaseHierarchy(detail.selectedOption) }}
                                options={diseaseList}>

                        </Select>
                      </div>
                    </FormField>
                    <FormField label="Graph view style:">
                      <div style={{width: '200px'}}>
                        <Select selectedOption={selectedGraphViewStyle}
                                options={[
                                  {label: "Concentric", value: "concentric"},
                                  {label: "Hierarchical", value: "hierarchical"},
                                  {label: "Cose-Bilkent", value: "cose"},
                                  {label: "Circular", value: "circle"},
                                ]}
                                onChange={({ detail}) => {
                                  setGraphViewStyle(detail.selectedOption.value);
                                  setSelectedGraphViewStyle(detail.selectedOption);
                                }}
                          >
                          </Select>
                      </div>
                    </FormField>
                  </SpaceBetween>
                </Form>
              </form>
              <GraphViewer data={graphData} isLoading={isDiseaseHierarchyLoading} viewStyle={graphViewStyle} allowExploration={true}></GraphViewer>
            </div>
          </Container>
                
            },
            {
              label: "How to build the data model",
              id: "architecture",
              content: 
              <Container
                header={
                  <Header
                    variant="h2"
                    description=
                    { <span>The reference architecture diagram below describes how you can create a data pipeline from source data to enriched structured data containing vector embeddings.
                        Click on the <img src="./assets/img/add-plus.png" className="inline-icon" alt="hotspot button"></img> icons to find out more information.
                      </span>
                    }
                  >
                    Building a sample data pipeline for graph vectors
                  </Header>}
                >
                  <AnnotationContext
                    i18nStrings={{
                      stepCounterText: (stepIndex, totalStepCount) =>
                        "Step " +
                        (stepIndex + 1) +
                        "/" +
                        totalStepCount,
                      taskTitle: (taskIndex, taskTitle) =>
                        "Task " + (taskIndex + 1) + ": " + taskTitle,
                      labelHotspot: (
                        openState,
                        stepIndex,
                        totalStepCount
                      ) =>
                        openState
                          ? "close annotation for step " +
                            (stepIndex + 1) +
                            " of " +
                            totalStepCount
                          : "open annotation for step " +
                            (stepIndex + 1) +
                            " of " +
                            totalStepCount,
                      nextButtonText: "Next",
                      previousButtonText: "Previous",
                      finishButtonText: "Close",
                      labelDismissAnnotation: "dismiss annotation"
                    }}
                    currentTutorial={{
                      tasks: [
                        {
                          title: "Prepare source data",
                          steps: [
                            {
                              title: "Prepare source data",
                              content: (
                                <>
                                  Prepare your structured and semi-structured data, such as journals, and academic and medical papers.
                                </>
                              ),
                              hotspotId: "source-data"
                            },
                            {
                              title: "Store data in S3",
                              content: (
                                <>
                                  The source data is stored into an S3 bucket, where it can be easily used as a source for natural language processing (NLP) services
                                  such as <Link href="https://aws.amazon.com/comprehend/medical/" external={true} externalIconAriaLabel="(opens in a new tab)">Amazon Comprehend Medical</Link>.
                                </>
                              ),
                              hotspotId: "source-bucket"
                            }
                          ]
                        },
                        {
                          title: "Identifying medical terminology",
                          steps: [
                            {
                              title: "",
                              content: (
                                <>
                                  Amazon Comprehend Medical provides machine learning capabilities to extract medical information from the source data quickly. The output
                                  of this process generates a strict schema that can be used within the graph data model.
                                </>
                              ),
                              hotspotId: "comprehend"
                            },
                            {
                              title: "",
                              content: (
                                <>
                                  The Comprehend Medical output is stored into another S3 bucket. 
                                </>
                              ),
                              hotspotId: "destination-bucket"
                            }
                          ]
                        },
                        {
                          title: "Generating embeddings for Neptune Analytics",
                          steps: [
                            {
                              title: "",
                              content: (
                                <>
                                  From here, we use the <Link href="https://docs.aws.amazon.com/bedrock/latest/userguide/titan-embedding-models.html" external={true}>Amazon Titan Embeddings G1 - Text</Link>
                                  large language model (LLM) to generate embeddings based on journal titles and abstracts. For this LLM, vector embeddings are generated at a length of 1,536.
                                </>
                              ),
                              hotspotId: "bedrock"
                            },
                            {
                              title: "",
                              content: (
                                <>
                                  The embeddings generated by Bedrock are written back to the source files, and stored in S3.
                                  We then call the <Link href="https://docs.aws.amazon.com/neptune-analytics/latest/userguide/neptune-load-batch.html" external={true}>neptune.load()</Link> command
                                  to bulk load the data into our <Link href="https://docs.aws.amazon.com/neptune-analytics/latest/userguide/what-is-neptune-analytics.html" external={true}>Amazon Neptune Analytics</Link> graph.
                                </>
                              ),
                              hotspotId: "neptune"
                            }
                          ]
                        }
                      ]
                    }}>
                        <img src="./assets/img/data_architecture.jpg" alt="Data Architecture"></img>

                        <div className="arch-img-map-source-data">
                          <Hotspot side="right" hotspotId="source-data"></Hotspot>
                        </div>
                        <div className="arch-img-map-source-bucket">
                          <Hotspot side="right" hotspotId="source-bucket"></Hotspot>
                        </div>
                        <div className="arch-img-map-comprehend">
                          <Hotspot side="right" hotspotId="comprehend"></Hotspot>
                        </div>
                        <div className="arch-img-map-destination-bucket">
                          <Hotspot side="right" hotspotId="destination-bucket"></Hotspot>
                        </div>
                        <div className="arch-img-map-bedrock">
                          <Hotspot side="right" hotspotId="bedrock"></Hotspot>
                        </div>
                        <div className="arch-img-map-neptune">
                          <Hotspot side="right" hotspotId="neptune"></Hotspot>
                        </div>
                  </AnnotationContext>
              </Container>
            }
          ]}
          >
          </Tabs>
        </Container>
      </div>
    )
}
